import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { User, Building2, Mail, Lock, Camera, Save, Loader2, Globe, MessageSquare, Key, Eye, EyeOff, Wallet, Clock, TrendingUp, DollarSign, ArrowDownRight, ArrowUpRight, Coins, AlertTriangle, ShoppingCart, ExternalLink, RefreshCw, HelpCircle, Filter, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { TOUR_KEY } from "../components/OnboardingTour";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MS_TEAMS_LINK = "https://teams.live.com/l/invite/FEAF6e2oWJeNwALLTk?v=g1";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user: authUser, updateUser: updateAuthUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", timezone: "" });
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);
  const [ringbaForm, setRingbaForm] = useState({ ringba_api_key: "", ringba_account_id: "", min_call_duration: 90, require_recording: true });
  const [savingRingba, setSavingRingba] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [billing, setBilling] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const fileRef = useRef(null);

  // 2FA state
  const [twoFA, setTwoFA] = useState({ enabled: false, loading: false, setupMode: false, qrCode: "", secret: "", verifyCode: "", setupPassword: "", disablePassword: "", disableCode: "" });

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const [profileRes, settingsRes, billingRes, txnRes, twoFARes] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/auth/user-settings`),
        axios.get(`${API}/billing/balance`),
        axios.get(`${API}/billing/transactions?limit=20`),
        axios.get(`${API}/auth/2fa-status`),
      ]);
      setUser(profileRes.data);
      setForm({ name: profileRes.data.name || "", company: profileRes.data.company || "", timezone: profileRes.data.timezone || "" });
      setRingbaForm({
        ringba_api_key: settingsRes.data.ringba_api_key || "",
        ringba_account_id: settingsRes.data.ringba_account_id || "",
        min_call_duration: settingsRes.data.min_call_duration ?? 90,
        require_recording: settingsRes.data.require_recording ?? true,
      });
      setBilling(billingRes.data);
      setTransactions(txnRes.data.transactions || []);
      setTwoFA(prev => ({ ...prev, enabled: twoFARes.data.is_enabled }));
    } catch (e) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/auth/profile`, form);
      setUser(res.data.user);
      // Update stored user
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({ ...stored, ...res.data.user }));
      toast.success("Profile updated");
    } catch (e) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (pwForm.newPw.length < 8) { toast.error("New password must be at least 8 characters with uppercase, lowercase, number, and special character"); return; }
    if (pwForm.newPw !== pwForm.confirm) { toast.error("Passwords do not match"); return; }
    setChangingPw(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.newPw,
      });
      toast.success("Password changed");
      setPwForm({ current: "", newPw: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPw(false);
    }
  };

  // 2FA handlers
  const handleSetup2FA = async () => {
    if (!twoFA.setupPassword) { toast.error("Enter your password to set up 2FA"); return; }
    setTwoFA(prev => ({ ...prev, loading: true }));
    try {
      const res = await axios.post(`${API}/auth/setup-2fa`, { password: twoFA.setupPassword });
      setTwoFA(prev => ({ ...prev, qrCode: res.data.qr_code, secret: res.data.secret, setupMode: true, loading: false }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to setup 2FA");
      setTwoFA(prev => ({ ...prev, loading: false }));
    }
  };

  const handleVerify2FASetup = async () => {
    if (twoFA.verifyCode.length !== 6) { toast.error("Enter the 6-digit code from your authenticator"); return; }
    setTwoFA(prev => ({ ...prev, loading: true }));
    try {
      await axios.post(`${API}/auth/verify-2fa-setup`, { totp_code: twoFA.verifyCode });
      toast.success("2FA enabled successfully!");
      setTwoFA({ enabled: true, loading: false, setupMode: false, qrCode: "", secret: "", verifyCode: "", setupPassword: "", disablePassword: "", disableCode: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
      setTwoFA(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDisable2FA = async () => {
    if (!twoFA.disablePassword || twoFA.disableCode.length !== 6) { toast.error("Enter your password and current 2FA code"); return; }
    setTwoFA(prev => ({ ...prev, loading: true }));
    try {
      await axios.post(`${API}/auth/disable-2fa`, { password: twoFA.disablePassword, totp_code: twoFA.disableCode });
      toast.success("2FA disabled");
      setTwoFA({ enabled: false, loading: false, setupMode: false, qrCode: "", secret: "", verifyCode: "", setupPassword: "", disablePassword: "", disableCode: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disable 2FA");
      setTwoFA(prev => ({ ...prev, loading: false }));
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("File too large (max 2MB)"); return; }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/auth/profile/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser((prev) => ({ ...prev, logo_url: res.data.logo_url }));
      updateAuthUser((prev) => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error("Failed to upload logo");
    }
  };

  const handleSaveRingba = async () => {
    setSavingRingba(true);
    try {
      await axios.put(`${API}/auth/user-settings`, ringbaForm);
      toast.success("Ringba settings saved");
    } catch (e) {
      toast.error("Failed to save Ringba settings");
    } finally {
      setSavingRingba(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="profile-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>Profile</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Manage your account settings</p>
        </div>
        <button onClick={() => fetchProfile()} className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-3 py-1.5 transition-all self-start sm:self-auto" data-testid="refresh-profile">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /><span className="text-sm">Refresh</span>
        </button>
      </div>

      {/* Profile Info */}
      <Card className="bg-slate-900/60 border-slate-800/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><User className="h-5 w-5" /> Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Logo Upload */}
          <div className="flex items-center gap-4">
            <div
              className="h-16 w-16 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-500/50 transition"
              onClick={() => fileRef.current?.click()}
              data-testid="logo-upload-area"
            >
              {user?.logo_url ? (
                <img src={user.logo_url} alt="Logo" className="h-full w-full object-cover" />
              ) : (
                <Camera className="h-6 w-6 text-slate-500" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-slate-300">Company Logo</p>
              <p className="text-xs text-slate-500">Click to upload (max 2MB, PNG/JPG)</p>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs uppercase tracking-wider flex items-center gap-1">
                <User className="h-3 w-3" /> Full Name
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-slate-950/60 border-slate-800/50 text-slate-100"
                data-testid="profile-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs uppercase tracking-wider flex items-center gap-1">
                <Building2 className="h-3 w-3" /> Company
              </Label>
              <Input
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="bg-slate-950/60 border-slate-800/50 text-slate-100"
                data-testid="profile-company"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs uppercase tracking-wider flex items-center gap-1">
                <Mail className="h-3 w-3" /> Email
              </Label>
              <Input value={user?.email || ""} disabled className="bg-slate-950/60 border-slate-800/50 text-slate-500" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs uppercase tracking-wider flex items-center gap-1">
                <Globe className="h-3 w-3" /> Timezone
              </Label>
              <select
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                className="w-full bg-slate-950/60 border border-slate-800/50 text-slate-100 rounded-md px-3 py-2 text-sm"
                data-testid="profile-timezone"
              >
                <option value="">Auto-detect</option>
                <option value="America/New_York">Eastern (ET)</option>
                <option value="America/Chicago">Central (CT)</option>
                <option value="America/Denver">Mountain (MT)</option>
                <option value="America/Los_Angeles">Pacific (PT)</option>
                <option value="America/Anchorage">Alaska (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii (HST)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500 pt-1">
            <span>Role: <span className="text-blue-400 font-medium uppercase">{user?.role || "user"}</span></span>
          </div>

          <Button
            onClick={handleSaveProfile}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="save-profile-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </CardContent>
      </Card>

      {/* Billing & Usage */}
      <Card className="bg-slate-900/60 border-slate-800/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Clock className="h-5 w-5" /> Minutes & Usage</CardTitle>
          <CardDescription>Your minutes balance and call analysis usage</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Low Credit Alert */}
          {billing?.low_balance && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-red-500/10 border-red-500/20 text-red-300">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">Low minutes! You have {billing?.minutes_balance ?? billing?.credits ?? 0} minutes remaining. Purchase more to continue analyzing calls.</span>
            </div>
          )}

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-950/60 border border-slate-800/50 rounded-xl p-3.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Clock className="h-3.5 w-3.5 text-green-400" />
                <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Balance</span>
              </div>
              <p className="text-xl font-bold text-green-400 font-mono" data-testid="billing-balance">
                {(billing?.minutes_balance ?? billing?.credits ?? 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
              </p>
              <p className="text-[10px] text-slate-600 mt-0.5">minutes remaining</p>
            </div>
            <div className="bg-slate-950/60 border border-slate-800/50 rounded-xl p-3.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-blue-400" />
                <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Purchased</span>
              </div>
              <p className="text-xl font-bold text-blue-400 font-mono" data-testid="billing-deposited">
                {(billing?.total_minutes_purchased ?? billing?.total_credits_purchased ?? 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
              </p>
              <p className="text-[10px] text-slate-600 mt-0.5">total minutes bought</p>
            </div>
            <div className="bg-slate-950/60 border border-slate-800/50 rounded-xl p-3.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <ArrowDownRight className="h-3.5 w-3.5 text-amber-400" />
                <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Used</span>
              </div>
              <p className="text-xl font-bold text-amber-400 font-mono" data-testid="billing-spent">
                {(billing?.total_minutes_used ?? billing?.total_credits_used ?? 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
              </p>
              <p className="text-[10px] text-slate-600 mt-0.5">minutes analyzed</p>
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs text-slate-500 bg-slate-950/40 rounded-lg px-4 py-2.5 border border-slate-800/30">
            <span>Rate: <span className="text-slate-300 font-mono font-medium">1 min/min</span></span>
          </div>

          {/* Buy Credits */}
          <div>
            <h3 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-1.5"><ShoppingCart className="h-3.5 w-3.5" /> Buy Minutes</h3>
            <button
              onClick={() => navigate("/buy-credits")}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-green-500/20 bg-green-500/5 hover:bg-green-500/15 transition text-sm group"
              data-testid="buy-credits-link"
            >
              <div className="h-9 w-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Coins className="h-5 w-5 text-green-400" />
              </div>
              <div className="text-left flex-1">
                <span className="text-green-400 font-semibold block">Buy Minutes with USDT</span>
                <span className="text-xs text-slate-500">Pay via TRC20 network</span>
              </div>
              <ExternalLink className="h-4 w-4 text-slate-500 group-hover:text-green-400 transition" />
            </button>
          </div>

          {/* Transaction History */}
          {transactions.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-2">Recent Transactions</h3>
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between bg-slate-950/40 rounded-lg px-3 py-2 border border-slate-800/20">
                    <div className="flex items-center gap-2.5">
                      {tx.amount < 0 ? (
                        <ArrowDownRight className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />
                      ) : (
                        <ArrowUpRight className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
                      )}
                      <div>
                        <p className="text-xs text-slate-300">{tx.description}</p>
                        <p className="text-[10px] text-slate-600">{new Date(tx.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-mono font-medium ${tx.amount < 0 ? "text-red-400" : "text-green-400"}`}>
                      {tx.amount < 0 ? "-" : "+"}{Math.abs(tx.amount).toFixed(1)} min
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="bg-slate-900/60 border-slate-800/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Lock className="h-5 w-5" /> Change Password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs uppercase tracking-wider">Current Password</Label>
              <Input
                type="password"
                value={pwForm.current}
                onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                className="bg-slate-950/60 border-slate-800/50 text-slate-100"
                data-testid="current-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs uppercase tracking-wider">New Password</Label>
              <Input
                type="password"
                value={pwForm.newPw}
                onChange={(e) => setPwForm({ ...pwForm, newPw: e.target.value })}
                className="bg-slate-950/60 border-slate-800/50 text-slate-100"
                data-testid="new-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs uppercase tracking-wider">Confirm New Password</Label>
              <Input
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                className="bg-slate-950/60 border-slate-800/50 text-slate-100"
                data-testid="confirm-new-password"
              />
            </div>
          </div>
          <Button
            onClick={handleChangePassword}
            disabled={changingPw}
            variant="outline"
            className="text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
            data-testid="change-password-btn"
          >
            {changingPw ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
            Change Password
          </Button>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card className="bg-slate-900/60 border-slate-800/50" data-testid="2fa-section">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            {twoFA.enabled
              ? "2FA is active. Your account is protected with Google Authenticator."
              : "Add an extra layer of security using Google Authenticator."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {twoFA.enabled ? (
            /* ====== DISABLE 2FA ====== */
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <ShieldCheck className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                <span className="text-sm text-emerald-300 font-medium">2FA is currently enabled</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Password</Label>
                  <Input
                    type="password"
                    value={twoFA.disablePassword}
                    onChange={(e) => setTwoFA(prev => ({ ...prev, disablePassword: e.target.value }))}
                    placeholder="Enter password"
                    className="bg-slate-950/60 border-slate-800/50 text-slate-100"
                    data-testid="2fa-disable-password"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-400 text-xs uppercase tracking-wider">Current 2FA Code</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={twoFA.disableCode}
                    onChange={(e) => setTwoFA(prev => ({ ...prev, disableCode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                    placeholder="6-digit code"
                    className="bg-slate-950/60 border-slate-800/50 text-slate-100"
                    data-testid="2fa-disable-code"
                  />
                </div>
              </div>
              <Button
                onClick={handleDisable2FA}
                disabled={twoFA.loading}
                variant="outline"
                className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                data-testid="disable-2fa-btn"
              >
                {twoFA.loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                Disable 2FA
              </Button>
            </div>
          ) : twoFA.setupMode ? (
            /* ====== VERIFY SETUP ====== */
            <div className="space-y-5">
              <div className="p-4 rounded-lg bg-slate-950/60 border border-slate-800/50 text-center space-y-3">
                <p className="text-sm text-slate-300 font-medium">Scan this QR code with Google Authenticator</p>
                <div className="inline-block p-3 bg-white rounded-xl">
                  <img src={`data:image/png;base64,${twoFA.qrCode}`} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Or enter this key manually:</p>
                  <code className="text-xs text-blue-400 bg-slate-800 px-3 py-1.5 rounded-md inline-block select-all font-mono tracking-wider" data-testid="2fa-manual-key">{twoFA.secret}</code>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Enter code from your authenticator</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={twoFA.verifyCode}
                  onChange={(e) => setTwoFA(prev => ({ ...prev, verifyCode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                  placeholder="6-digit code"
                  className="bg-slate-950/60 border-slate-800/50 text-slate-100 max-w-[200px]"
                  data-testid="2fa-verify-code"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleVerify2FASetup}
                  disabled={twoFA.loading || twoFA.verifyCode.length !== 6}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid="verify-2fa-setup-btn"
                >
                  {twoFA.loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  Enable 2FA
                </Button>
                <Button
                  onClick={() => setTwoFA(prev => ({ ...prev, setupMode: false, qrCode: "", secret: "", verifyCode: "", setupPassword: "" }))}
                  variant="outline"
                  className="text-slate-400 border-slate-700 hover:bg-slate-800"
                  data-testid="cancel-2fa-setup"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            /* ====== START SETUP ====== */
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Confirm your password to begin</Label>
                <Input
                  type="password"
                  value={twoFA.setupPassword}
                  onChange={(e) => setTwoFA(prev => ({ ...prev, setupPassword: e.target.value }))}
                  placeholder="Enter your password"
                  className="bg-slate-950/60 border-slate-800/50 text-slate-100 max-w-[300px]"
                  data-testid="2fa-setup-password"
                />
              </div>
              <Button
                onClick={handleSetup2FA}
                disabled={twoFA.loading || !twoFA.setupPassword}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="setup-2fa-btn"
              >
                {twoFA.loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                Set Up 2FA
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ringba Integration */}
      <Card className="bg-slate-900/60 border-slate-800/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Key className="h-5 w-5" /> Ringba Integration</CardTitle>
          <CardDescription>Configure your Ringba API credentials for call import & analysis</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs uppercase tracking-wider">API Key</Label>
              <div className="relative">
                <Input
                  type={showApiKey ? "text" : "password"}
                  value={ringbaForm.ringba_api_key}
                  onChange={(e) => setRingbaForm({ ...ringbaForm, ringba_api_key: e.target.value })}
                  className="bg-slate-950/60 border-slate-800/50 text-slate-100 pr-10"
                  placeholder="Your Ringba API key"
                  data-testid="ringba-api-key"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-xs uppercase tracking-wider">Account ID</Label>
              <Input
                value={ringbaForm.ringba_account_id}
                onChange={(e) => setRingbaForm({ ...ringbaForm, ringba_account_id: e.target.value })}
                className="bg-slate-950/60 border-slate-800/50 text-slate-100"
                placeholder="e.g. RAac7c2a..."
                data-testid="ringba-account-id"
              />
            </div>
          </div>
          
          {/* Call Import Rules */}
          <div className="pt-3 border-t border-slate-800/50">
            <p className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
              <Filter className="h-4 w-4 text-cyan-400" /> Call Import Rules
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Minimum Call Duration (seconds)</Label>
                <Input
                  type="number"
                  min="0"
                  max="600"
                  value={ringbaForm.min_call_duration}
                  onChange={(e) => setRingbaForm({ ...ringbaForm, min_call_duration: parseInt(e.target.value) || 0 })}
                  className="bg-slate-950/60 border-slate-800/50 text-slate-100"
                  placeholder="90"
                  data-testid="min-call-duration"
                />
                <p className="text-[10px] text-slate-500">Set to 0 to import all calls regardless of length</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-400 text-xs uppercase tracking-wider">Require Call Recording</Label>
                <button
                  onClick={() => setRingbaForm({ ...ringbaForm, require_recording: !ringbaForm.require_recording })}
                  className={`flex items-center gap-3 w-full rounded-lg border px-4 py-2.5 text-left transition-all ${
                    ringbaForm.require_recording
                      ? "bg-green-500/10 border-green-500/30 text-green-400"
                      : "bg-slate-950/60 border-slate-800/50 text-slate-500"
                  }`}
                  data-testid="require-recording-toggle"
                >
                  <div className={`relative w-9 h-5 rounded-full flex-shrink-0 transition-colors ${ringbaForm.require_recording ? "bg-green-500" : "bg-slate-700"}`}>
                    <div className="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform" style={{ transform: ringbaForm.require_recording ? "translateX(16px)" : "translateX(2px)" }} />
                  </div>
                  <span className="text-sm">{ringbaForm.require_recording ? "Only import calls with recordings" : "Import all calls (with or without recordings)"}</span>
                </button>
              </div>
            </div>
          </div>

          <Button
            onClick={handleSaveRingba}
            disabled={savingRingba}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="save-ringba-btn"
          >
            {savingRingba ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Ringba Settings
          </Button>
        </CardContent>
      </Card>

      {/* Support & Help */}
      <Card className="bg-slate-900/60 border-slate-800/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><MessageSquare className="h-5 w-5" /> Support & Help</CardTitle>
          <CardDescription>Need help? Contact our support team or take the app tour</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-3">
          <a
            href={MS_TEAMS_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600/20 border border-indigo-500/30 text-indigo-400 rounded-lg hover:bg-indigo-600/30 transition text-sm font-medium"
            data-testid="support-teams-link"
          >
            <MessageSquare className="h-4 w-4" />
            Chat with us on Microsoft Teams
          </a>
          <Button
            variant="outline"
            onClick={() => {
              if (authUser) localStorage.removeItem(`${TOUR_KEY}_${authUser.id}`);
              navigate("/dashboard");
              setTimeout(() => window.dispatchEvent(new Event("callintell-restart-tour")), 600);
            }}
            className="inline-flex items-center gap-2 border-emerald-500/30 text-emerald-400 hover:bg-emerald-600/10 hover:text-emerald-300"
            data-testid="retake-tour-btn"
          >
            <HelpCircle className="h-4 w-4" />
            Take App Tour
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
